import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader, Database } from "lucide-react";
import axios from "../../config/axios";
import toast from "react-hot-toast";
import DynamicForm from "../../components/DynamicForm";

/**
 * ConnectorSetup Component
 * Simple single-step setup for creating a connector instance
 */
const ConnectorSetup = () => {
  const { connectorId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [connector, setConnector] = useState(null);
  const [connectorSpec, setConnectorSpec] = useState(null);
  const [connectionName, setConnectionName] = useState("");
  const [sourceConfig, setSourceConfig] = useState({});

  useEffect(() => {
    fetchConnectorDetails();
  }, [connectorId]);

  const fetchConnectorDetails = async () => {
    try {
      setLoading(true);

      // Fetch connector metadata and specification in parallel
      const [connectorResponse, specResponse] = await Promise.all([
        axios.get(`/api/connectors/${connectorId}`),
        axios.get(`/api/connectors/${connectorId}/specification`),
      ]);

      setConnector(connectorResponse.data.connector);
      setConnectorSpec(specResponse.data);
      setConnectionName(`${connectorResponse.data.connector.name} Connection`);
    } catch (error) {
      console.error("Failed to fetch connector details:", error);
      toast.error("Failed to load connector information");
      navigate("/connectors");
    } finally {
      setLoading(false);
    }
  };

  const createInstance = async () => {
    try {
      // Validate connection name
      if (!connectionName.trim()) {
        toast.error("Please enter a connection name");
        return;
      }

      // Validate required fields from connector spec
      if (connectorSpec?.specification?.required) {
        const missingFields = connectorSpec.specification.required.filter(
          (field) => !sourceConfig[field],
        );

        if (missingFields.length > 0) {
          toast.error(
            `Please fill in required fields: ${missingFields.join(", ")}`,
          );
          return;
        }
      }

      setLoading(true);

      // Create connector instance with source configuration
      const response = await axios.post(
        `/api/connectors/${connectorId}/instances`,
        {
          name: connectionName,
          sourceConfig: sourceConfig,
        },
      );

      const instance = response.data.instance;

      toast.success("Connection created successfully");

      // Navigate to connection detail page
      navigate(`/datasources/${instance.id}`);
    } catch (error) {
      console.error("Failed to create instance:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to create connection",
      );
    } finally {
      setLoading(false);
    }
  };

  const canCreate = () => {
    if (!connectionName.trim()) return false;
    if (!connectorSpec?.specification) return false;

    const required = connectorSpec.specification.required || [];
    return required.every((field) => sourceConfig[field]);
  };

  if (loading && !connector) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/connectors")}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Connectors
        </button>

        <div className="flex items-center">
          {connector?.iconUrl && (
            <img
              src={connector.iconUrl}
              alt={connector.name}
              className="w-12 h-12 mr-4 rounded"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Setup {connector?.name}
            </h1>
            <p className="text-gray-600">
              Configure your {connector?.name} connection
            </p>
          </div>
        </div>
      </div>

      {/* Setup Form */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="space-y-6">
          {/* Connection Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Connection Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder="e.g., Google Analytics Production"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">
              Give your connection a descriptive name to identify it easily
            </p>
          </div>

          {/* Configuration Fields */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="w-5 h-5 mr-2 text-primary-600" />
              Configuration
            </h3>

            {!connectorSpec ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-6 h-6 text-primary-600 animate-spin" />
                <span className="ml-3 text-gray-600">
                  Loading configuration...
                </span>
              </div>
            ) : (
              <DynamicForm
                spec={connectorSpec.specification}
                values={sourceConfig}
                onChange={(fieldName, value) => {
                  setSourceConfig((prev) => ({
                    ...prev,
                    [fieldName]: value,
                  }));
                }}
                errors={{}}
              />
            )}
          </div>

          {/* Create Button */}
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={createInstance}
              disabled={!canCreate() || loading}
              className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center font-medium"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Creating Connection...
                </>
              ) : (
                <>
                  Create Connection
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectorSetup;
